import { useState, useEffect } from 'react'
import { X, Upload } from 'lucide-react'
import { supabase } from '../services/supabase'
import Button from './Button'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
    imagem_url: string | null
    duracao_estimada: number
    nivel: string
    status: string
    ordem_exibicao: number
}

interface ModalCursoProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    curso?: Curso | null
    onShowToast?: (type: 'success' | 'error', title: string, message?: string) => void
}

export default function ModalCurso({ isOpen, onClose, onSuccess, curso, onShowToast }: ModalCursoProps) {
    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        duracao_estimada: '',
        nivel: 'iniciante',
        status: 'ativo',
        ordem_exibicao: '0'
    })
    const [imagemFile, setImagemFile] = useState<File | null>(null)
    const [imagemPreview, setImagemPreview] = useState<string>('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (curso) {
            setFormData({
                titulo: curso.titulo,
                descricao: curso.descricao || '',
                duracao_estimada: curso.duracao_estimada.toString(),
                nivel: curso.nivel,
                status: curso.status,
                ordem_exibicao: curso.ordem_exibicao.toString()
            })
            setImagemPreview(curso.imagem_url || '')
        } else {
            resetForm()
        }
    }, [curso, isOpen])

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            duracao_estimada: '',
            nivel: 'iniciante',
            status: 'ativo',
            ordem_exibicao: '0'
        })
        setImagemFile(null)
        setImagemPreview('')
        setError('')
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setImagemFile(file)
            const reader = new FileReader()
            reader.onload = (e) => {
                setImagemPreview(e.target?.result as string)
            }
            reader.readAsDataURL(file)
        }
    }

    const uploadImage = async (file: File): Promise<string | null> => {
        try {
            const fileExt = file.name.split('.').pop()
            const fileName = `${Math.random()}.${fileExt}`
            const filePath = `cursos/${fileName}`

            const { error: uploadError } = await supabase.storage
                .from('imagens')
                .upload(filePath, file)

            if (uploadError) throw uploadError

            const { data } = supabase.storage
                .from('imagens')
                .getPublicUrl(filePath)

            return data.publicUrl
        } catch (err) {
            console.error('Erro ao fazer upload da imagem:', err)
            return null
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            // Validações
            if (!formData.titulo.trim()) {
                setError('Título é obrigatório')
                return
            }

            if (!formData.duracao_estimada || parseInt(formData.duracao_estimada) <= 0) {
                setError('Duração estimada deve ser maior que zero')
                return
            }

            // Upload da imagem se houver
            let imagemUrl = imagemPreview
            if (imagemFile) {
                const uploadedUrl = await uploadImage(imagemFile)
                if (uploadedUrl) {
                    imagemUrl = uploadedUrl
                }
            }

            const cursoData = {
                titulo: formData.titulo.trim(),
                descricao: formData.descricao.trim() || null,
                duracao_estimada: parseInt(formData.duracao_estimada),
                nivel: formData.nivel,
                status: formData.status,
                ordem_exibicao: parseInt(formData.ordem_exibicao),
                imagem_url: imagemUrl || null
            }

            if (curso) {
                // Atualizar curso existente
                const { error } = await supabase
                    .from('cursos')
                    .update(cursoData)
                    .eq('id', curso.id)

                if (error) throw error
                onShowToast?.('success', 'Curso atualizado com sucesso!')
            } else {
                // Criar novo curso
                const { error } = await supabase
                    .from('cursos')
                    .insert(cursoData)

                if (error) throw error
                onShowToast?.('success', 'Curso criado com sucesso!')
            }

            onSuccess()
            onClose()
            resetForm()
        } catch (err) {
            console.error('Erro ao salvar curso:', err)
            setError('Erro ao salvar curso. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {curso ? 'Editar Curso' : 'Novo Curso'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Erro */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Título */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Título do Curso *
                        </label>
                        <input
                            type="text"
                            value={formData.titulo}
                            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Ex: Vendas Imobiliárias Básicas"
                            required
                        />
                    </div>

                    {/* Descrição */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Descrição
                        </label>
                        <textarea
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                            rows={4}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Descreva o conteúdo e objetivos do curso..."
                        />
                    </div>

                    {/* Grid de campos */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Duração Estimada */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Duração (minutos) *
                            </label>
                            <input
                                type="number"
                                value={formData.duracao_estimada}
                                onChange={(e) => setFormData({ ...formData, duracao_estimada: e.target.value })}
                                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                placeholder="120"
                                min="1"
                                required
                            />
                        </div>

                        {/* Nível */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Nível *
                            </label>
                            <select
                                value={formData.nivel}
                                onChange={(e) => setFormData({ ...formData, nivel: e.target.value })}
                                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                required
                            >
                                <option value="iniciante">Iniciante</option>
                                <option value="intermediario">Intermediário</option>
                                <option value="avancado">Avançado</option>
                            </select>
                        </div>

                        {/* Ordem de Exibição */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Ordem de Exibição
                            </label>
                            <input
                                type="number"
                                value={formData.ordem_exibicao}
                                onChange={(e) => setFormData({ ...formData, ordem_exibicao: e.target.value })}
                                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                placeholder="0"
                                min="0"
                            />
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Status *
                        </label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            required
                        >
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                            <option value="em_breve">Em Breve</option>
                        </select>
                    </div>

                    {/* Upload de Imagem */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Imagem do Curso
                        </label>
                        <div className="space-y-4">
                            {/* Preview da imagem */}
                            {imagemPreview && (
                                <div className="relative w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                                    <img
                                        src={imagemPreview}
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            )}

                            {/* Input de upload */}
                            <div className="flex items-center justify-center w-full">
                                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
                                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="font-semibold">Clique para upload</span> ou arraste e solte
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            PNG, JPG, GIF até 10MB
                                        </p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Botões */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="btn_secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="btn_add"
                            loading={loading}
                            disabled={loading}
                        >
                            {curso ? 'Atualizar' : 'Criar'} Curso
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}
